import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { InsertLink } from '@material-ui/icons';
import cx from 'classnames';
import Autosuggest from 'react-autosuggest';
import { motion, AnimatePresence } from "framer-motion";
import axiosUI from '../../../axiosUI';
import {
  NODES,
  LINK_TYPES,
  LINK_TARGETS,
  NOTIFICATIONS,
} from '../../../constants';
import { EventEmitter } from '../../../utils';
import PopupWrapper from '../../PopupWrapper';

const EVENT_NAME = 'linkEventName';
class Link extends Component {
  static propTypes = {
    editor: PropTypes.object,
    value: PropTypes.object,
  };

  constructor(props) {
    super(props);

    this.state = {
      showPopup: false,
      externalLinkValue: '',
      linkType: LINK_TYPES.EXTERNAL,
      suggestions: [],
      articleSuggestionValue: '',
      linkTarget: LINK_TARGETS.SAME_WINDOW,
      articles: {},
      [EVENT_NAME]: '',
    };

    this.getSuggestions = this.getSuggestions.bind(this);
    this.getSuggestionValue = this.getSuggestionValue.bind(this);
    this.renderSuggestion = this.renderSuggestion.bind(this);
    this.onSuggestionsFetchRequested = this.onSuggestionsFetchRequested.bind(
      this
    );
    this.onSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(
      this
    );
    this.onArticleSuggestionChange = this.onArticleSuggestionChange.bind(this);
    this.hasLinks = this.hasLinks.bind(this);
    this.wrapLink = this.wrapLink.bind(this);
    this.unwrapLink = this.unwrapLink.bind(this);
    this.togglePopup = this.togglePopup.bind(this);
    this.onChangeHandler = this.onChangeHandler.bind(this);
    this.onSuggestionSelected = this.onSuggestionSelected.bind(this);
  }

  componentDidMount() {
    EventEmitter.listen('open-popup--link', () => {
      this.togglePopup();
    });
  }

  getSuggestions(value) {
    const inputValue = value.trim().toLowerCase();
    const inputLength = inputValue.length;

    if (inputLength === 0) {
      return [];
    }

    const { locale, sales_channel } = this.props;
    const market = `${sales_channel}_${locale}`;
    const marketArticles = this.state.articles[market] || [];

    const matches = marketArticles.filter(article => {
      return article.title.toLowerCase().includes(inputValue);
    });

    return matches.slice(0, 10);
  }

  getSuggestionValue(suggestion) {
    return suggestion.value;
  }

  renderSuggestion(suggestion) {
    return <div>{suggestion.title}</div>;
  }

  onSuggestionsFetchRequested({ value }) {
    this.setState({
      suggestions: this.getSuggestions(value),
    });
  }

  onSuggestionsClearRequested() {
    this.setState({ suggestions: [] });
  }

  onArticleSuggestionChange(event, { newValue }) {
    this.setState({ articleSuggestionValue: newValue });
  }

  hasLinks() {
    return this.props.value.inlines.some(inline => inline.type === NODES.LINK);
  }

  wrapLink() {
    const linkToConsider =
      this.state.linkType === LINK_TYPES.EXTERNAL
        ? this.state.externalLinkValue
        : './' + this.state.articleSuggestionValue; // Appending `./` to support `:` in relative URL paths

    // remove all existing links before adding a new one
    this.unwrapLink();

    this.props.editor.wrapInline({
      type: NODES.LINK,
      data: {
        href: linkToConsider,
        target: this.state.linkTarget,
        eventName: this.state.linkEventName,
      },
    });

    this.setState({ externalLinkValue: '', articleSuggestionValue: '' });

    this.togglePopup();
  }

  unwrapLink() {
    this.props.editor.unwrapInline(NODES.LINK);
  }

  togglePopup() {
    this.setState(prevState => ({ showPopup: !prevState.showPopup }));
  }

  onChangeHandler(event) {
    const { locale, sales_channel } = this.props;
    const { target: { name, value } } = event;

    if (name === 'linkType') {
      const blockLinkTypeArticleSelect = (
        value === LINK_TYPES.ARTICLE
        && (!locale || !sales_channel)
      );

      if (blockLinkTypeArticleSelect) {
        return this.props.createNotification({
          message: `Please select a market to link published articles`,
          type: NOTIFICATIONS.TYPE.INFO,
          timeout: 3000,
        });
      }

      this.fetchAndSetMarketArticlesOnState(sales_channel, locale);
    }

    this.setState({ [name]: value });
  }

  onSuggestionSelected(event) {
    // adding this to avoid auto close of popups
    event.stopPropagation();
  }

  fetchAndSetMarketArticlesOnState = async (sales_channel, locale) => {
    const market = `${sales_channel}_${locale}`;
    const articlesArePresentInState = this.state.articles[market];
    if (!articlesArePresentInState) {
      try {
        const url = `/api/articles`;
        const { data } = await axiosUI(url, {
          params: {
            locale,
            salesChannel: sales_channel,
          },
        });

        const publishedArticles = data.filter(article => article.totalPublished > 0);
        const selectOptions = publishedArticles.map(article => {
          return { title: article.name, value: `${article.articleSlug}.html` };
        });

        this.setState({
          articles: {
            ...this.state.articles,
            [market]: selectOptions,
          },
        });
      } catch (err) {
        this.props.createNotification({
          message: `Failed to fetch articles`,
          type: NOTIFICATIONS.TYPE.ERROR,
          timeout: 3000,
        });

        console.log(err);
      }
    }
  };

  render() {
    const autoSuggestInputProps = {
      placeholder: 'Enter article title to search',
      value: this.state.articleSuggestionValue,
      onChange: this.onArticleSuggestionChange,
      className: 'link__input normal-border-sm w-64 rounded-sm p-1',
    };
    const disableSubmitButton = !(this.state.linkType === LINK_TYPES.EXTERNAL
      ? this.state.externalLinkValue.trim()
      : this.state.articleSuggestionValue.trim());
    return (
      <PopupWrapper
        isOpen={this.state.showPopup}
        togglePopup={this.togglePopup}>
        <span className="icon__wrapper icon__wrapper--link">
          <button
            title="Link"
            className={cx('icon__button', {
              active: this.state.showPopup,
            })}
            onClick={this.togglePopup}>
            <InsertLink />
          </button>
          <AnimatePresence>
            {this.state.showPopup &&
            <motion.div
              initial={{ opacity: 0, y: '80%' }}
              animate={{ opacity: 1, y: '100%' }}
              exit={{ opacity: 0, y: '80%' }}
              transition={{ ease: [0.18, 0.89, 0.39, 1.4], duration: .3 }}
              className="icon__popup icon__popup--link icon__popup--with_autosuggest">
              <label
                htmlFor="linkTarget"
                className="flex font-bold items-center justify-between mb-6 text-sm">
                Target
                <select
                  value={this.state.linkTarget}
                  onChange={this.onChangeHandler}
                  name="linkTarget"
                  className="bg-white normal-border-sm text-xs cursor-pointer">
                  <option value={LINK_TARGETS.SAME_WINDOW}>Same Window</option>
                  <option value={LINK_TARGETS.NEW_WINDOW}>New Window</option>
                </select>
              </label>
              <label
                htmlFor="link_external"
                className={cx('mr-10 text-sm cursor-pointer', {
                  'font-bold': LINK_TYPES.EXTERNAL === this.state.linkType,
                })}>
                External Link
                <input
                  className="input--radio align-middle ml-3"
                  type="radio"
                  id="link_external"
                  name="linkType"
                  value={LINK_TYPES.EXTERNAL}
                  checked={LINK_TYPES.EXTERNAL === this.state.linkType}
                  onChange={this.onChangeHandler}
                />
              </label>
              <label
                htmlFor="link_article"
                className={cx('text-sm cursor-pointer', {
                  'font-bold': LINK_TYPES.ARTICLE === this.state.linkType,
                })}>
                Article Link
                <input
                  className="input--radio align-middle ml-3"
                  type="radio"
                  id="link_article"
                  name="linkType"
                  value={LINK_TYPES.ARTICLE}
                  checked={LINK_TYPES.ARTICLE === this.state.linkType}
                  onChange={this.onChangeHandler}
                />
              </label>

              <div className="mt-6 mb-6 whitespace-no-wrap">
                {this.state.linkType === LINK_TYPES.EXTERNAL ? (
                  <input
                    placeholder="Enter external link"
                    name="externalLinkValue"
                    className="link__input normal-border-sm w-64 rounded-sm mr-5 p-1"
                    value={this.state.externalLinkValue}
                    onChange={this.onChangeHandler}
                    type="text"
                  />
                ) : (
                  <Autosuggest
                    alwaysRenderSuggestions
                    suggestions={this.state.suggestions}
                    onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                    onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                    getSuggestionValue={this.getSuggestionValue}
                    renderSuggestion={this.renderSuggestion}
                    inputProps={autoSuggestInputProps}
                    onSuggestionSelected={this.onSuggestionSelected}
                  />
                )}
              </div>

              <section className="flex items-center justify-between mb-6 text-sm whitespace-no-wrap">
                <label htmlFor={EVENT_NAME} className="font-bold">
                  Event Name (Optional)
                </label>
                <input
                  type="text"
                  name={EVENT_NAME}
                  id={EVENT_NAME}
                  className="ml-3"
                  onChange={this.onChangeHandler}
                />
              </section>

              <button
                className={cx(
                  'normal-button rounded p-1 font-bold cursor-pointer',
                  { disable: disableSubmitButton }
                )}
                onClick={this.wrapLink}>
                Add Link
              </button>

            </motion.div>}
          </AnimatePresence>
        </span>
      </PopupWrapper>
    );
  }
}

export default  Link;
